import classnames from "classnames";
import React, { useEffect, useMemo, useRef } from "react";
import { Spinner } from "reactstrap";

import { isDefined } from "@utils/helpers";

import { StoreDetailsModel } from "@redux/modules/api/data/stores/@types/storeDetails.model";
import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { Button } from "@components/General/Button/button.component";
import { Divider } from "@components/General/Divider/divider.component";
import StoreDetails from "@components/Store/StoreDetails/storeDetails.component";

import { MAX_STORES, STORE_INCREMENT } from "../settings";
import "./storeList.scss";

type Props = {
    onShowMore: () => void;
    loading: boolean;
    stores: StoreDetailsModel[];
    storesLoaded: boolean;
    error: any;
    amount: number;
    shopCode?: string;
    onSelect: (store: StoreDetailsModel) => void;
    isOnlyAvailableInStore: boolean;
    displayStockLevel: boolean;
};

const StoreList = ({
    onShowMore,
    loading,
    stores,
    storesLoaded,
    error,
    amount,
    shopCode,
    onSelect,
    isOnlyAvailableInStore,
    displayStockLevel,
}: Props) => {
    const hideShowMore =
        amount === MAX_STORES || amount >= stores?.length || !stores?.length;
    const showStores = !loading && !error && stores && stores.length > 0;
    const showNoStores =
        (!loading && storesLoaded && stores.length === 0) || error;
    const { t } = useGranite();
    const storeListRef = useRef<HTMLDivElement>(null);

    const visibleStores = useMemo(
        () => stores.slice(0, stores.length >= amount ? amount : stores.length),
        [stores, amount],
    );

    useEffect(() => {
        const totalGroups = Math.ceil(visibleStores.length / STORE_INCREMENT);
        const startOfLastGroup = (totalGroups - 1) * STORE_INCREMENT;
        const storeCodeToScrollTo = visibleStores.at(startOfLastGroup)?.code;

        if (!isDefined(storeCodeToScrollTo)) return;

        storeListRef.current
            ?.querySelector(
                `[data-store-code="${storeCodeToScrollTo.toString()}"]`,
            )
            ?.scrollIntoView();
    }, [visibleStores]);

    return (
        <React.Fragment>
            {showStores && <Divider marginTop={"0"} />}
            <div className="storePicker-storeList__stores" ref={storeListRef}>
                {loading && <Spinner color="primary" />}
                {showNoStores && (
                    <div>{t("storePicker.text.noStoresFound")}</div>
                )}
                {showStores &&
                    visibleStores.map((store, index) => (
                        <StoreDetails
                            key={index}
                            store={store}
                            selectedStore={shopCode}
                            onSelect={() => onSelect(store)}
                            isOnlyAvailableInStore={isOnlyAvailableInStore}
                            displayStockLevel={displayStockLevel}
                        />
                    ))}
            </div>
            <span
                className={classnames(
                    "storePicker-storeList__divider",
                    showStores ? "mb-1 mb-md-3" : "my-1 my-md-3",
                )}
            />
            {!hideShowMore && (
                <Button
                    test="storeList-button-showMore"
                    color={"link"}
                    className={"px-0 text-left"}
                    type={"button"}
                    onClick={onShowMore}
                >
                    {t("general.storepicker.label.showMore")}
                    <i className={"las la-angle-down ml-2"} />
                </Button>
            )}
        </React.Fragment>
    );
};

export default StoreList;
